import { Component, inject, signal, OnInit, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { TextareaComponent } from '../../../../shared/components/ui/textarea-input/textarea.component';
import { MicroServiceHttpService } from '../../../../core/services/microServiceHttp.service';

export type Tab = 'default_settings' | 'default_prompt' | 'ai_providers';

export interface providerAPIData {
  pid: number;
  keyname: string;
  name: string;
  apiKeyEnc: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  modelCount: number;
}

export interface modelAPIData {
  providerId: number;
  providerKeyname: string;
  providerName: string;
  pid: number;
  model: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface DefaultPromptAPIData {
  pid: number;
  label: string;
  prompt: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

@Component({
  selector: 'app-default-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2, ButtonComponent, TextareaComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './default-settings.component.html',
})
export class DefaultSettingsComponent implements OnInit {
  private readonly microServiceHttpService = inject(MicroServiceHttpService);

  selectedTab = signal<Tab>('ai_providers');
  providers = signal<providerAPIData[]>([]);
  expandedProviderId = signal<number | null>(null);
  providerModels = signal<Record<number, modelAPIData[]>>({});
  modelsLoading = signal<number | null>(null);

  newKeyname = signal('');
  newName = signal('');
  newApiKeyEnc = signal('');

  addModelDialogOpen = signal(false);
  addModelProviderId = signal<number | null>(null);
  newModelModel = signal('');
  newModelDescription = signal('');

  confirmDeleteType = signal<'provider' | 'model' | 'prompt' | null>(null);
  confirmDeleteProviderId = signal<number | null>(null);
  confirmDeleteModelId = signal<number | null>(null);
  confirmDeletePromptId = signal<number | null>(null);
  confirmDeleteDisplayName = signal('');

  defaultPrompts = signal<DefaultPromptAPIData[]>([]);
  promptsLoading = signal(false);
  newPromptLabel = signal('');
  newPromptText = signal('');

  editPromptDialogOpen = signal(false);
  editPromptId = signal<number | null>(null);
  editPromptLabel = signal('');
  editPromptText = signal('');
  
  promptFormTouched = signal<Record<string, boolean>>({});
  editPromptTouched = signal<Record<string, boolean>>({});

  promptFormErrors = computed(() => {
    const label = this.newPromptLabel().trim();
    const prompt = this.newPromptText().trim();
    return {
      label: !label ? 'Label is required' : null,
      prompt: !prompt ? 'Prompt is required' : null,
    };
  });

  editPromptErrors = computed(() => {
    const label = this.editPromptLabel().trim();
    const prompt = this.editPromptText().trim();
    return {
      label: !label ? 'Label is required' : null,
      prompt: !prompt ? 'Prompt is required' : null,
    };
  });

  activePrompts = computed(() => this.defaultPrompts().filter((p) => !p.deletedAt));
  deletedPrompts = computed(() => this.defaultPrompts().filter((p) => p.deletedAt));

  setTab(tab: Tab): void {
    this.selectedTab.set(tab);
    if (tab === 'default_prompt' && this.defaultPrompts().length === 0) {
      this.getDefaultPromptsApi();
    }
  }

  markPromptTouched(field: string): void {
    this.promptFormTouched.update((s) => ({ ...s, [field]: true }));
  }

  markEditPromptTouched(field: string): void {
    this.editPromptTouched.update((s) => ({ ...s, [field]: true }));
  }

  toggleProvider(pid: number): void {
    if (this.expandedProviderId() === pid) {
      this.expandedProviderId.set(null);
      return;
    }
    this.expandedProviderId.set(pid);
    if (this.providerModels()[pid] === undefined) {
      this.getProviderModelsApi(pid);
    }
  }

  isExpanded(pid: number): boolean {
    return this.expandedProviderId() === pid;
  }

  getModelsForProvider(pid: number): modelAPIData[] {
    return this.providerModels()[pid] ?? [];
  }

  addProviderFromForm(): void {
    const keyname = this.newKeyname().trim();
    const name = this.newName().trim().slice(0, 255);
    const apiKey = this.newApiKeyEnc().trim();
    if (!keyname || !name) return;
    this.createProviderApi({ keyname, name, apiKey });
  }

  openAddModelDialog(providerId: number): void {
    this.addModelProviderId.set(providerId);
    this.newModelModel.set('');
    this.newModelDescription.set('');
    this.addModelDialogOpen.set(true);
  }

  closeAddModelDialog(): void {
    this.addModelDialogOpen.set(false);
    this.addModelProviderId.set(null);
  }

  submitAddModel(): void {
    const providerId = this.addModelProviderId();
    const model = this.newModelModel().trim().slice(0, 255);
    if (!providerId || !model) return;
    const description = this.newModelDescription().trim();
    this.createModelApi(providerId, { model, description });
    this.closeAddModelDialog();
  }

  addModelDialogProviderName(): string {
    const pid = this.addModelProviderId();
    return this.providers().find((p) => p.pid === pid)?.name ?? '';
  }

  openConfirmDeleteProvider(pid: number, event: Event): void {
    event.stopPropagation();
    const provider = this.providers().find((p) => p.pid === pid);
    this.confirmDeleteType.set('provider');
    this.confirmDeleteProviderId.set(pid);
    this.confirmDeleteModelId.set(null);
    this.confirmDeleteDisplayName.set(provider?.name ?? '');
  }

  openConfirmDeleteModel(providerId: number, model: modelAPIData): void {
    this.confirmDeleteType.set('model');
    this.confirmDeleteProviderId.set(providerId);
    this.confirmDeleteModelId.set(model.pid);
    this.confirmDeleteDisplayName.set(model.model);
  }

  openConfirmDeletePrompt(prompt: DefaultPromptAPIData): void {
    this.confirmDeleteType.set('prompt');
    this.confirmDeletePromptId.set(prompt.pid);
    this.confirmDeleteDisplayName.set(prompt.label || `Prompt #${prompt.pid}`);
  }

  closeConfirmDelete(): void {
    this.confirmDeleteType.set(null);
    this.confirmDeleteProviderId.set(null);
    this.confirmDeleteModelId.set(null);
    this.confirmDeletePromptId.set(null);
    this.confirmDeleteDisplayName.set('');
  }

  confirmDeleteMessage(): string {
    const type = this.confirmDeleteType();
    const name = this.confirmDeleteDisplayName();
    if (type === 'provider') return `Remove provider "${name}"? This will remove all its models.`;
    if (type === 'model') return `Remove model "${name}"?`;
    if (type === 'prompt') return `Are you sure you want to delete this prompt? This will remove it from all associated chatbots using it as a base.`;
    return '';
  }

  confirmDelete(): void {
    const type = this.confirmDeleteType();
    if (type === 'provider') {
      const pid = this.confirmDeleteProviderId();
      if (pid != null) this.deleteProviderApi(pid);
    } else if (type === 'model') {
      const modelId = this.confirmDeleteModelId();
      const providerId = this.confirmDeleteProviderId();
      if (modelId != null && providerId != null) this.deleteModelApi(modelId, providerId);
    } else if (type === 'prompt') {
      const promptId = this.confirmDeletePromptId();
      if (promptId != null) this.deleteDefaultPromptApi(promptId);
    }
    this.closeConfirmDelete();
  }

  addPromptFromForm(): void {
    const errs = this.promptFormErrors();
    if (errs.label || errs.prompt) {
      this.promptFormTouched.set({ label: true, prompt: true });
      return;
    }
    const label = this.newPromptLabel().trim();
    const prompt = this.newPromptText().trim();
    this.createDefaultPromptApi({ label, prompt });
  }

  openEditPromptDialog(prompt: DefaultPromptAPIData): void {
    this.editPromptId.set(prompt.pid);
    this.editPromptLabel.set(prompt.label || '');
    this.editPromptText.set(prompt.prompt);
    this.editPromptDialogOpen.set(true);
  }

  closeEditPromptDialog(): void {
    this.editPromptDialogOpen.set(false);
    this.editPromptId.set(null);
    this.editPromptText.set('');
    this.editPromptTouched.set({});
  }

  submitEditPrompt(): void {
    const errs = this.editPromptErrors();
    if (errs.label || errs.prompt) {
      this.editPromptTouched.set({ label: true, prompt: true });
      return;
    }
    const id = this.editPromptId();
    const prompt = this.editPromptText().trim();
    const label = this.editPromptLabel().trim();
    if (!id) return;
    this.updateDefaultPromptApi(id, prompt, label);
    this.closeEditPromptDialog();
  }

  restorePrompt(prompt: DefaultPromptAPIData): void {
    this.restoreDefaultPromptApi(prompt.pid);
  }

  ngOnInit(): void {
    this.getProvidersApi();
  }

  // ── API Methods ──────────────────────────────────────────────────────────────

  getProvidersApi(): void {
    this.microServiceHttpService.get('whitelabel', 'default-config/llm-providers', {
      successMessage: 'Providers fetched successfully',
    }).subscribe({
      next: (response: any) => {
        this.providers.set(response.data ?? []);
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  getProviderModelsApi(providerId: number): void {
    this.modelsLoading.set(providerId);
    this.microServiceHttpService.get('whitelabel', `default-config/llm-models/${providerId}`, {
      showToast: false,
      showLoader: false
    }).subscribe({
      next: (response: any) => {
        this.providerModels.update((map) => ({ ...map, [providerId]: response.data ?? [] }));
        this.modelsLoading.set(null);
      },
      error: (error: any) => {
        console.error(error);
        this.modelsLoading.set(null);
      },
    });
  }

  createProviderApi(provider: { keyname: string; name: string; apiKey: string }): void {
    // A blank key is "none": prism-new rejects '' (the gateway skipped it).
    const body = { ...provider, apiKey: provider.apiKey || undefined };
    this.microServiceHttpService.post('whitelabel', 'default-config/llm-providers', body, {
      successMessage: 'Provider created successfully',
    }).subscribe({
      next: () => {
        this.newKeyname.set('');
        this.newName.set('');
        this.newApiKeyEnc.set('');
        this.getProvidersApi();
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  deleteProviderApi(providerId: number): void {
    this.microServiceHttpService.delete('whitelabel', `default-config/llm-providers/${providerId}`, {
      successMessage: 'Provider deleted successfully',
    }).subscribe({
      next: () => {
        if (this.expandedProviderId() === providerId) this.expandedProviderId.set(null);
        this.providerModels.update((map) => {
          const updated = { ...map };
          delete updated[providerId];
          return updated;
        });
        this.getProvidersApi();
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  createModelApi(providerId: number, model: { model: string; description: string }): void {
    const payload = {
      providerId,
      model: model.model,
      description: model.description || undefined,
    };
    this.microServiceHttpService.post('whitelabel', `default-config/llm-models`, payload, {
      successMessage: 'Model created successfully',
    }).subscribe({
      next: () => {
        this.getProviderModelsApi(providerId);
        this.getProvidersApi();
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  deleteModelApi(modelId: number, providerId: number): void {
    this.microServiceHttpService.delete('whitelabel', `default-config/llm-models/${modelId}`, {
      successMessage: 'Model deleted successfully',
    }).subscribe({
      next: () => {
        this.getProviderModelsApi(providerId);
        this.getProvidersApi();
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  getDefaultPromptsApi(): void {
    this.promptsLoading.set(true);
    this.microServiceHttpService.get('whitelabel', 'default-config/default-prompts', {
      showToast: false,
    }).subscribe({
      next: (response: any) => {
        this.defaultPrompts.set(response.data ?? []);
        this.promptsLoading.set(false);
      },
      error: (error: any) => {
        console.error(error);
        this.promptsLoading.set(false);
      },
    });
  }

  createDefaultPromptApi(data: { label?: string; prompt: string }): void {
    this.microServiceHttpService.post('whitelabel', 'default-config/default-prompts', data, {
      successMessage: 'Prompt created successfully',
    }).subscribe({
      next: () => {
        this.newPromptLabel.set('');
        this.newPromptText.set('');
        this.promptFormTouched.set({});
        this.getDefaultPromptsApi();
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  updateDefaultPromptApi(id: number, prompt: string, label: string): void {
    const updatePrompt$ = this.microServiceHttpService.put('whitelabel', `default-config/default-prompts/${id}`, { prompt }, {
      showToast: false,
    });
    const updateLabel$ = this.microServiceHttpService.put('whitelabel', `default-config/default-prompts/${id}/label`, { label }, {
      showToast: false,
    });

    updatePrompt$.subscribe({
      next: () => {
        updateLabel$.subscribe({
          next: () => {
            this.getDefaultPromptsApi();
          },
          error: (error: any) => console.error(error),
        });
      },
      error: (error: any) => console.error(error),
    });
  }

  deleteDefaultPromptApi(id: number): void {
    this.microServiceHttpService.delete('whitelabel', `default-config/default-prompts/${id}`, {
      successMessage: 'Prompt deleted successfully',
    }).subscribe({
      next: () => this.getDefaultPromptsApi(),
      error: (error: any) => console.error(error),
    });
  }

  restoreDefaultPromptApi(id: number): void {
    this.microServiceHttpService.post('whitelabel', `default-config/default-prompts/${id}/restore`, {}, {
      successMessage: 'Prompt restored successfully',
    }).subscribe({
      next: () => this.getDefaultPromptsApi(),
      error: (error: any) => console.error(error),
    });
  }
}
